import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { FileText, MessageCircle, Heart, ClipboardCheck, Users } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { formatRelativeTime } from '../../utils/time';
import type { AppNotification, NotificationType } from '../../types';

interface NotificationCardProps {
  notification: AppNotification;
  onPress: () => void;
}

const ICON_MAP: Record<NotificationType, LucideIcon> = {
  newPost: FileText,
  comment: MessageCircle,
  like: Heart,
  checklistReminder: ClipboardCheck,
  communityUpdate: Users,
};

export function NotificationCard({ notification, onPress }: NotificationCardProps) {
  const { colors } = useTheme();

  const Icon = ICON_MAP[notification.type];
  const timestamp = formatRelativeTime(notification.createdAt, { suffix: 'ago' });
  // Unread reads from the ink dot and the ink title, not from a tinted row
  // and a tinted icon square: a green wash on an alert about a *like* is a
  // category signal firing on something that has no category.
  const iconFg = notification.read ? colors.textTertiary : colors.textSecondary;

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.container, { borderBottomColor: colors.border }]}
      activeOpacity={0.7}
      testID="notification-card"
      // Same hook as FeedPostItem's postId: the visual audit hides every row
      // that isn't a seeded fixture, since the audit user's real alerts are
      // live data and would otherwise decide the screen's length.
      // @ts-expect-error dataSet is a react-native-web prop (visual-audit hook); not in RN's type defs
      dataSet={{ notificationId: notification.id }}
      accessibilityRole="button"
      accessibilityLabel={notification.title}
      accessibilityState={{ selected: !notification.read }}>
      {/* Bare icon, no tinted box — the ListItem rule. */}
      <Icon size={18} color={iconFg} strokeWidth={1.5} style={styles.icon} />
      <View style={styles.content}>
        <Text style={[styles.title, { color: colors.text }]} numberOfLines={1}>
          {notification.title}
        </Text>
        <Text style={[styles.body, { color: colors.textSecondary }]} numberOfLines={2}>
          {notification.body}
        </Text>
        {/* textSecondary, not textTertiary: the faint grey is sub-AA and this
            is content, not a placeholder (axe: 2.72 against 4.5). */}
        <Text style={[styles.timestamp, { color: colors.textSecondary }]}>{timestamp}</Text>
      </View>
      {!notification.read && <View style={[styles.unreadDot, { backgroundColor: colors.text }]} />}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: SPACING.smd,
  },
  icon: {
    flexShrink: 0,
    marginTop: 3,
  },
  content: {
    flex: 1,
    gap: SPACING.xxs + 1,
  },
  title: {
    ...TYPOGRAPHY.button,
    lineHeight: 20,
  },
  body: {
    ...TYPOGRAPHY.bodySmall,
  },
  timestamp: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.xxs,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: SPACING.xs,
    flexShrink: 0,
  },
});
